import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';

import { X, Reply, Paperclip, Eye, MessageCircle } from 'lucide-react';
import { getStatusColor } from '../../utils';
import { Tooltip } from '../ui/tooltip';
import LoaderOverlay from '../common/LoaderOverlay';
import SourceCombobox from '../common/SourceCombobox';

import api from '../../services/api';
import { IMAGE_API_BASE_URL } from '../../config/api';

export interface SupportTicket {
  id: number;
  ticketId: string;
  subject: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  email: string;
  accountNumber?: string;
  issueDescription: string;
  message?: string;
  attachments:
    | Array<{
        filename: string;
        originalName: string;
        path: string;
        size: number;
        mimeType: string;
        documentType: string;
      }>
    | string[];
  status: 'Open' | 'In-progress' | 'Resolved' | 'Closed';
  userId: number;
  assignedTo?: number;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: number;
    fullName: string;
    email: string;
  };
  assignedUser?: {
    id: number;
    fullName: string;
    email: string;
  };
}

interface SupportTicketModalProps {
  ticket: SupportTicket | null;
  isOpen: boolean;
  onClose: () => void;
  onReplySubmit?: (ticketId: number, reply: string, status: string) => void;
  viewMode?: boolean;
  onViewModeChange?: (viewMode: boolean) => void;
  loading?: boolean;
}

const statusOptions = [
  { value: 'Open', label: 'Open' },
  { value: 'In-progress', label: 'In Progress' },
  { value: 'Resolved', label: 'Resolved' },
  { value: 'Closed', label: 'Closed' },
];

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getBackendHost = (): string => {
  const envUrl =
    IMAGE_API_BASE_URL ||
    process.env.REACT_APP_IMAGE_API_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    'http://localhost:5000';
  return envUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
};

const getAttachmentUrl = (attachmentPath: string): string => {
  if (!attachmentPath) return '';

  if (
    attachmentPath.startsWith('http://') ||
    attachmentPath.startsWith('https://') ||
    attachmentPath.startsWith('blob:') ||
    attachmentPath.startsWith('data:')
  ) {
    return attachmentPath;
  }

  const backendHost = getBackendHost();
  let normalizedPath = attachmentPath.replace(/\\/g, '/');

  if (
    !normalizedPath.startsWith('/uploads/') &&
    !normalizedPath.startsWith('http')
  ) {
    if (normalizedPath.startsWith('uploads/')) {
      normalizedPath = '/' + normalizedPath;
    } else {
      normalizedPath =
        '/uploads/' +
        (normalizedPath.startsWith('/')
          ? normalizedPath.slice(1)
          : normalizedPath);
    }
  }

  const cleanPath = normalizedPath.startsWith('/')
    ? normalizedPath
    : '/' + normalizedPath;

  if (cleanPath.startsWith(backendHost)) {
    return cleanPath;
  }

  return `${backendHost}${cleanPath}`;
};

const isImageFile = (fileName: string): boolean => {
  if (!fileName) return false;
  if (fileName.startsWith('image/')) return true;
  const imageExtensions = ['jpg', 'jpeg', 'png', 'webp'];
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  return imageExtensions.includes(extension);
};

const getFileName = (attachmentPath: string): string => {
  const parts = attachmentPath.split(/[\\/]/);
  return parts[parts.length - 1] || attachmentPath;
};

const normalizeAttachments = (attachments: any) => {
  if (!attachments || !Array.isArray(attachments)) return [];

  return attachments.map(attachment => {
    if (typeof attachment === 'string') {
      return {
        id: attachment,
        path: attachment,
        name: getFileName(attachment),
        size: 0,
        type: isImageFile(attachment) ? 'image' : 'document',
        url: getAttachmentUrl(attachment),
      };
    }
    const path =
      attachment.path ||
      (attachment.filename ? `/uploads/${attachment.filename}` : '');
    const name =
      attachment.originalName ||
      attachment.filename ||
      attachment.name ||
      getFileName(path);
    return {
      id: attachment.path || attachment.filename || attachment.id,
      path: path,
      name: name,
      size: attachment.size || 0,
      type:
        attachment.mimeType?.startsWith('image/') || isImageFile(name)
          ? 'image'
          : 'document',
      url: getAttachmentUrl(path),
    };
  });
};

const AttachmentImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
  onError?: () => void;
}> = ({ src, alt, className, onError }) => {
  const [blobUrl, setBlobUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    let createdUrl = '';

    if (!src) {
      setLoading(false);
      return;
    }

    if (src.startsWith('blob:') || src.startsWith('data:')) {
      setBlobUrl(src);
      setLoading(false);
      return;
    }

    const loadImageBlob = async () => {
      try {
        setLoading(true);
        const response = await api.get(src, { responseType: 'blob' });
        if (response.data && isMounted) {
          createdUrl = URL.createObjectURL(response.data);
          setBlobUrl(createdUrl);
        }
      } catch (err) {
        console.error('Error fetching attachment blob:', err);
        if (isMounted) {
          setBlobUrl(src);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadImageBlob();

    return () => {
      isMounted = false;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className="w-full h-full min-h-[100px] bg-muted/30 flex items-center justify-center">
        <LoaderOverlay
          isLoading={loading}
          loadingText="Loading Support Ticket..."
        />
      </div>
    );
  }

  return (
    <img
      src={blobUrl || src}
      alt={alt}
      onError={onError}
      className={className}
    />
  );
};

const SupportTicketModal: React.FC<SupportTicketModalProps> = ({
  ticket,
  isOpen,
  onClose,
  onReplySubmit,
  viewMode = false,
  onViewModeChange,
  loading = false,
}) => {
  const [replyText, setReplyText] = useState(ticket?.message || '');
  const [editStatus, setEditStatus] = useState<string>(
    ticket?.status || 'Open'
  );
  const [prevTicketId, setPrevTicketId] = useState<number | undefined>(
    ticket?.id
  );
  const [previewModalUrl, setPreviewModalUrl] = useState<string | null>(null);
  const [previewModalTitle, setPreviewModalTitle] = useState<string>('');

  if (ticket && ticket.id !== prevTicketId) {
    setPrevTicketId(ticket.id);
    setReplyText(ticket.message || '');
    setEditStatus(ticket.status || 'Open');
  }

  const editAttachments = useMemo(() => {
    return normalizeAttachments(ticket?.attachments);
  }, [ticket?.attachments]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleReplySubmit = () => {
    if (ticket && replyText.trim() && onReplySubmit) {
      if (ticket.status === 'Resolved' || ticket.status === 'Closed') {
        toast.error('Cannot edit resolved or closed tickets');
        return;
      }
      onReplySubmit(ticket.id, replyText, editStatus);
      setReplyText('');
    }
  };

  const handleClose = () => {
    setReplyText('');
    onClose();
  };

  const handleDownloadAttachment = (attachment: {
    url: string;
    name: string;
  }) => {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen || !ticket) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-background rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col m-4">
        <div className="flex flex-row items-start justify-between space-y-0 pb-4 border-b p-4 pt-6">
          <div className="flex-1 pr-8">
            <h2 className="text-xl font-semibold">{ticket.subject}</h2>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={getStatusColor(ticket.category)}>
                {ticket.category.toUpperCase()}
              </Badge>
              <Badge className={getStatusColor(ticket.priority, 'priority')}>
                {ticket.priority.toUpperCase()}
              </Badge>
              <Badge
                className={getStatusColor(
                  viewMode ? ticket.status : editStatus
                )}
              >
                {(viewMode ? ticket.status : editStatus)
                  .replace('-', ' ')
                  .toUpperCase()}
              </Badge>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 py-4 px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div className="flex items-start">
              <span className="text-muted-foreground min-w-[120px] inline-block">
                Ticket ID:
              </span>
              <span className="font-medium">{ticket.ticketId}</span>
            </div>
            <div className="flex items-start">
              <span className="text-muted-foreground min-w-[120px] inline-block">
                Category:
              </span>
              <span className="font-medium capitalize">{ticket.category}</span>
            </div>
            <div className="flex items-start">
              <span className="text-muted-foreground min-w-[120px] inline-block">
                User:
              </span>
              <span className="font-medium">
                {ticket.creator?.fullName || ''}
              </span>
            </div>
            <div className="flex items-start">
              <span className="text-muted-foreground min-w-[120px] inline-block">
                Email:
              </span>
              <span className="font-medium">{ticket.creator?.email || ''}</span>
            </div>
            <div className="flex items-start">
              <span className="text-muted-foreground min-w-[120px] inline-block">
                Account Number:
              </span>
              <span className="font-medium">{ticket.accountNumber || ''}</span>
            </div>
            <div className="flex items-start">
              <span className="text-muted-foreground min-w-[120px] inline-block">
                Assigned To:
              </span>
              <span className="font-medium">
                {ticket.assignedUser?.fullName || 'Unassigned'}
              </span>
            </div>
          </div>

          <div className="border-t pt-4 space-y-2">
            <h4 className="font-semibold text-sm">Description</h4>
            <Textarea
              readOnly
              value={ticket.issueDescription}
              className="w-full p-3 border border-border rounded-md bg-muted/50 text-black min-h-[100px] resize-none cursor-default text-sm leading-relaxed disabled:opacity-100 disabled:text-black"
            />
          </div>

          {ticket.message && (
            <div className="border-t pt-4 space-y-2">
              <h4 className="font-semibold text-sm flex items-center gap-2 text-blue-700">
                <MessageCircle className="h-4 w-4 text-blue-600" />
                Admin Response
              </h4>
              <div className="w-full p-3 border border-blue-200/80 rounded-md bg-blue-50/40 text-foreground text-sm leading-relaxed whitespace-pre-line break-words">
                {ticket.message}
              </div>
            </div>
          )}

          {/* Existing Attachments */}
          {editAttachments && editAttachments.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Attachments ({editAttachments.length})
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {editAttachments.map(attachment => (
                  <div key={attachment.id} className="relative group">
                    {attachment.type === 'image' ? (
                      <div
                        className="relative aspect-square rounded-lg overflow-hidden border border-border cursor-pointer hover:shadow-md transition-shadow bg-muted/20"
                        onClick={() => {
                          setPreviewModalUrl(attachment.url);
                          setPreviewModalTitle(attachment.name);
                        }}
                        title="Click to view preview"
                      >
                        <AttachmentImage
                          src={attachment.url}
                          alt={attachment.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-medium gap-1">
                          <Eye className="h-5 w-5" />
                          View Preview
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2">
                          <p className="text-xs text-white truncate font-medium">
                            {attachment.name}
                          </p>
                          {attachment.size > 0 && (
                            <p className="text-[10px] text-white/80">
                              {formatFileSize(attachment.size)}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <Tooltip
                        content={
                          <div className="flex flex-col items-start gap-1">
                            <span className="font-medium text-xs">
                              {attachment.name}
                            </span>
                            {attachment.size > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {formatFileSize(attachment.size)}
                              </span>
                            )}
                          </div>
                        }
                      >
                        <div
                          className="aspect-square rounded-lg border border-border bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors p-3"
                          onClick={() => handleDownloadAttachment(attachment)}
                        >
                          <Paperclip className="h-8 w-8 text-gray-400 mb-2" />
                          <p className="text-xs text-gray-600 text-center line-clamp-2">
                            {attachment.name}
                          </p>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="border-t p-4">
          {viewMode ? (
            <div className="flex justify-end">
              {!(
                ticket.status === 'Resolved' || ticket.status === 'Closed'
              ) && (
                <Button
                  variant="default"
                  onClick={() => onViewModeChange?.(false)}
                >
                  {ticket.message ? 'Edit Response' : 'Edit Reply'}
                </Button>
              )}{' '}
            </div>
          ) : (
            <div className="w-full space-y-3">
              {(ticket.status === 'Resolved' || ticket.status === 'Closed') && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-sm text-amber-800">
                    This ticket is {ticket.status}. Editing and status changes
                    are not allowed for resolved or closed tickets.
                  </p>
                </div>
              )}
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">
                  {ticket.message ? 'Edit Response' : 'Reply'}
                </h4>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Status:</label>
                  <SourceCombobox
                    id="ticket-status"
                    items={statusOptions.map(option => option.label)}
                    placeholder="Select status"
                    value={
                      statusOptions.find(option => option.value === editStatus)
                        ?.label || ''
                    }
                    onChange={value => {
                      const option = statusOptions.find(
                        item => item.label === value
                      );
                      if (option) setEditStatus(option.value);
                    }}
                    disabled={
                      ticket.status === 'Resolved' || ticket.status === 'Closed'
                    }
                    className="w-32"
                    emptyText="No statuses found."
                  />
                </div>
              </div>
              <Textarea
                className="w-full p-3 border border-border rounded-md bg-background min-h-[100px]"
                placeholder="Type your reply or response here..."
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                disabled={
                  ticket.status === 'Resolved' || ticket.status === 'Closed'
                }
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleReplySubmit}
                  disabled={
                    !replyText.trim() ||
                    ticket.status === 'Resolved' ||
                    ticket.status === 'Closed'
                  }
                >
                  <Reply className="h-4 w-4 mr-2" />
                  {ticket.message ? 'Update Response' : 'Send Reply'}
                </Button>
              </div>
            </div>
          )}
        </div>
        <LoaderOverlay isLoading={loading} loadingText="Sending reply...">
          <div className="opacity-0 pointer-events-none">
            {/* Invisible content to maintain layout */}
          </div>
        </LoaderOverlay>
      </div>

      {/* In-App Image Preview Lightbox Modal */}
      {previewModalUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative bg-background rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col p-4">
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <h3 className="text-sm font-semibold truncate pr-4">
                {previewModalTitle || 'Attachment Preview'}
              </h3>
              <button
                onClick={() => setPreviewModalUrl(null)}
                className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 w-full max-h-[75vh] flex items-center justify-center p-2 bg-black/5 rounded-lg overflow-auto mt-3">
              {isImageFile(previewModalTitle) ? (
                <AttachmentImage
                  src={previewModalUrl}
                  alt={previewModalTitle}
                  className="max-w-full max-h-[70vh] object-contain rounded shadow"
                />
              ) : (
                <iframe
                  src={previewModalUrl}
                  title={previewModalTitle}
                  className="w-full h-[65vh] border-0 rounded"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketModal;
